import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import RenderMonitor from '../components/RenderMonitor';

const SETTINGS_ID = 1;
const MAX_PROMPT_LENGTH = 20000;

function PropelionAIPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        console.log('[PROPELION_AI][DB] SELECT reflection_llm_settings');
        const { data, error: fetchError } = await supabase
          .from('reflection_llm_settings')
          .select('system_prompt, updated_at')
          .eq('id', SETTINGS_ID)
          .maybeSingle();

        if (!mounted) return;
        if (fetchError) {
          setError(fetchError.message || 'Failed to load system prompt.');
          return;
        }
        setSystemPrompt(data?.system_prompt ?? '');
        setUpdatedAt(data?.updated_at || '');
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || 'Failed to load system prompt.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      if (systemPrompt.length > MAX_PROMPT_LENGTH) {
        setError(`System prompt must be ${MAX_PROMPT_LENGTH} characters or fewer.`);
        return;
      }

      console.log('[PROPELION_AI][DB] UPSERT reflection_llm_settings');
      const { data, error: saveError } = await supabase
        .from('reflection_llm_settings')
        .upsert(
          {
            id: SETTINGS_ID,
            system_prompt: systemPrompt,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'id' }
        )
        .select('updated_at')
        .single();

      if (saveError) {
        setError(saveError.message || 'Failed to save system prompt.');
        return;
      }
      setUpdatedAt(data?.updated_at || new Date().toISOString());
      setNotice('System prompt saved.');
    } catch (e) {
      setError(e?.message || 'Failed to save system prompt.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="modern-container modern-container-narrow">
        <div className="modern-loading">Loading Propelion AI settings…</div>
      </div>
    );
  }

  return (
    <div className="modern-container modern-container-narrow">
      <RenderMonitor componentName="PropelionAIPage" />

      <div className="modern-flex modern-justify-between modern-items-center modern-mb-6">
        <div>
          <h1 className="modern-title">Propelion AI</h1>
          <p className="modern-subtitle" style={{ marginBottom: 0 }}>
            Global system prompt used when generating reflection follow-up questions.
          </p>
        </div>
        <button
          type="button"
          className="modern-button modern-button-secondary"
          onClick={() => navigate('/admin/clients')}
        >
          Back to Admin
        </button>
      </div>

      <div className="modern-card">
        <label htmlFor="system-prompt" className="modern-heading" style={{ display: 'block' }}>
          System prompt
        </label>
        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>
          Describes the general context and behaviour of the LLM for reflection follow-ups.
        </p>
        <textarea
          id="system-prompt"
          className="modern-textarea"
          rows={16}
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          disabled={saving}
          placeholder="Enter the global system prompt…"
          style={{ width: '100%', resize: 'vertical', minHeight: 280 }}
        />
        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
          {systemPrompt.length.toLocaleString()} / {MAX_PROMPT_LENGTH.toLocaleString()} characters
          {updatedAt ? (
            <span style={{ marginLeft: 12 }}>
              Last saved: {new Date(updatedAt).toLocaleString()}
            </span>
          ) : null}
        </div>
        {error ? <div className="modern-error" style={{ marginTop: 12 }}>{error}</div> : null}
        {notice ? (
          <div style={{ marginTop: 12, color: '#15803d', fontSize: 14 }}>{notice}</div>
        ) : null}
        <div className="modern-flex modern-gap-4" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="modern-button modern-button-success"
            onClick={handleSave}
            disabled={saving || systemPrompt.length > MAX_PROMPT_LENGTH}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PropelionAIPage;
